import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { type ExtendedFormDefinition, useCloneForm, useForms } from '@features/forms';
import { mapPatientInfoResponseToPatient, usePatientInfoQuery } from '@features/patients';
import { useSubjectLanguageVariants, useUserState } from '@features/user';
import type { FormWithRevisionSummary } from '@store/generated/forms';
import { useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import type { Patient } from '../../types/types';
import { MODAL_TYPES } from '../modals';

import { FormPreviewSlidePane } from './FormPreviewSlidePane';
import { FormSelectorSlidePane } from './FormSelectorSlidePane';
import { ReviewFormsSlidePane } from './ReviewFormsSlidePane';

/**
 * Handle interface for imperative API
 */
export interface SendFormsSlidePanelsHandle {
  open: () => void;
  close: () => void;
}

/**
 * Props for SendFormsSlidePanels component
 */
interface SendFormsSlidePanelsProps {
  /** Patient ID to send forms to */
  patientId: string;
  /** Controlled mode: whether the panel is open */
  open?: boolean;
  /** Callback when the panel is closed */
  onClose?: () => void;
  /** Callback when forms are successfully sent */
  onSuccess?: () => void;
  /** Optional pre-fetched patient data */
  patient?: Patient;
  /**
   * Where the send flow was launched from. Threaded through to the
   * `form_sent` analytics event so we can measure conversion (e.g. an
   * `assessment_trajectory_ghost` CTA -> a successful send).
   */
  source?: string;
  /** Optional forms already chosen by a domain-specific launcher. */
  initialFormIds?: string[];
}

const EMPTY_INITIAL_FORM_IDS: string[] = [];

/**
 * Transform V2 forms API response to legacy format for compatibility
 */
function transformV2FormsToLegacy(v2Forms: FormWithRevisionSummary[]): ExtendedFormDefinition[] {
  return v2Forms.map(item => {
    const { form, current_revision } = item;

    let type: 'public' | 'user' | 'group' = 'user';
    if (form.owner_scope === 'public') {
      type = 'public';
    } else if (form.owner_scope === 'org') {
      type = 'group';
    }

    return {
      form_id: form.id || '',
      title: current_revision?.title || 'Untitled Form',
      description: current_revision?.description || '',
      validationSchema: undefined,
      groups: [],
      type,
      form_type: form.form_type as 'standard' | 'test',
    };
  });
}

/**
 * Portable container component for the form sending flow.
 * Manages the three slide panes: FormSelector, FormPreview, and ReviewForms.
 *
 * @example
 * // Controlled mode
 * const [isOpen, setIsOpen] = useState(false);
 * <SendFormsSlidePanels
 *   patientId={patientId}
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => console.log('Forms sent')}
 * />
 *
 * @example
 * // Imperative mode
 * const ref = useRef<SendFormsSlidePanelsHandle>(null);
 * <SendFormsSlidePanels ref={ref} patientId={patientId} />
 * // Later: ref.current?.open()
 */
export const SendFormsSlidePanels = forwardRef<
  SendFormsSlidePanelsHandle,
  SendFormsSlidePanelsProps
>(({ patientId, open = false, onClose, onSuccess, patient: providedPatient, source, initialFormIds = EMPTY_INITIAL_FORM_IDS }, ref) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const userState = useUserState();
  const { singular: subjectLanguage, singularCap: subjectLanguageCap } =
    useSubjectLanguageVariants();
  const { open: openModal } = useModal();
  const { mutate: cloneForm } = useCloneForm();

  // Panel visibility states
  const [isFormSelectorOpen, setIsFormSelectorOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isReviewPaneOpen, setIsReviewPaneOpen] = useState(false);

  // Form selection state
  const [selectedFormIds, setSelectedFormIds] = useState<string[]>([]);
  const [previewFormId, setPreviewFormId] = useState<string | null>(null);
  const [isCloningForm, setIsCloningForm] = useState(false);

  // Patient data — read through RTK Query so autosave edits on the patient page
  // (which invalidate the PatientInfo / PatientClinicalInfo tags) propagate here
  // instead of leaving the modal with a stale one-shot snapshot. When a live
  // patient is supplied via props, skip the query and use it directly.
  const {
    data: patientInfoResponse,
    isLoading: isPatientInfoLoading,
    isError: isPatientInfoError,
  } = usePatientInfoQuery({ patientId }, { skip: !!providedPatient || !patientId });

  const patient = useMemo<Patient | null>(() => {
    if (providedPatient) return providedPatient;
    if (!patientId || !patientInfoResponse) return null;
    return mapPatientInfoResponseToPatient(patientInfoResponse, patientId);
  }, [providedPatient, patientId, patientInfoResponse]);

  const isLoadingPatient = !providedPatient && isPatientInfoLoading;

  // Surface a snackbar when the patient query fails — the previous manual fetch
  // did this in its catch block; RTK Query's isLoading settles to false on error
  // so without this the modal would silently render with no patient.
  useEffect(() => {
    if (isPatientInfoError) {
      enqueueSnackbar(`Failed to load ${subjectLanguage} data`, { variant: 'error' });
    }
  }, [isPatientInfoError, enqueueSnackbar, subjectLanguage]);

  // Pagination state for forms
  const [formListPage, setFormListPage] = useState(1);
  const FORMS_PER_PAGE = 100;

  // Fetch available forms
  const { forms: rawV2Forms } = useForms(
    {
      page: formListPage,
      pageSize: FORMS_PER_PAGE,
    },
    {
      skip: !isFormSelectorOpen,
    },
  );

  // Transform V2 forms to legacy format
  const availableForms = useMemo(() => transformV2FormsToLegacy(rawV2Forms), [rawV2Forms]);

  // Handle controlled mode
  useEffect(() => {
    if (open && !isFormSelectorOpen) {
      handleOpen();
    } else if (!open && isFormSelectorOpen) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Internal open handler
  const handleOpen = useCallback(() => {
    // Check if onboarding is completed
    if (!userState?.onboardingSteps?.patient_communication_sms_onboarding_completed) {
      openModal(MODAL_TYPES.PATIENT_COMMUNICATION_ONBOARDING, {});
      return;
    }

    setIsFormSelectorOpen(true);
    setFormListPage(1);
    setSelectedFormIds(initialFormIds);
  }, [initialFormIds, userState, openModal]);

  // Internal close handler
  const handleClose = useCallback(() => {
    setIsFormSelectorOpen(false);
    setIsPreviewOpen(false);
    setIsReviewPaneOpen(false);
    setSelectedFormIds([]);
    setPreviewFormId(null);
    onClose?.();
  }, [onClose]);

  // Expose imperative API
  useImperativeHandle(
    ref,
    () => ({
      open: handleOpen,
      close: handleClose,
    }),
    [handleOpen, handleClose],
  );

  // Handle form selection toggle
  const handleFormSelect = useCallback((formId: string) => {
    setSelectedFormIds(prev =>
      prev.includes(formId) ? prev.filter(id => id !== formId) : [...prev, formId],
    );
  }, []);

  // Proceed to review pane
  const handleCreateFormSession = useCallback(() => {
    setIsReviewPaneOpen(true);
  }, []);

  // Handle form preview
  const handlePreviewForm = useCallback((formId: string) => {
    setPreviewFormId(formId);
    setIsPreviewOpen(true);
  }, []);

  // Close preview
  const handleClosePreview = useCallback(() => {
    setIsPreviewOpen(false);
    setPreviewFormId(null);
  }, []);

  // Handle form customization
  const handleCustomizeForm = useCallback(async () => {
    if (!previewFormId) return;

    setIsCloningForm(true);
    try {
      await cloneForm(
        previewFormId,
        { new_scope: 'provider' },
        {
          onSuccess: data => {
            const newFormId = data?.form?.form?.id;
            if (newFormId) {
              setIsPreviewOpen(false);
              navigate(`/form-builder/${newFormId}`);
            } else {
              enqueueSnackbar('Failed to clone form - no form ID returned', {
                variant: 'error',
              });
            }
          },
          onError: error => {
            console.error('Error cloning form:', error);
            enqueueSnackbar('Failed to customize form', { variant: 'error' });
          },
        },
      );
    } catch (error) {
      console.error('Error cloning form:', error);
      enqueueSnackbar('Failed to customize form', { variant: 'error' });
    } finally {
      setIsCloningForm(false);
    }
  }, [previewFormId, cloneForm, navigate, enqueueSnackbar]);

  // Handle successful form send
  const handleSendSuccess = useCallback(() => {
    setIsReviewPaneOpen(false);
    setIsFormSelectorOpen(false);
    setSelectedFormIds([]);
    onSuccess?.();
    onClose?.();
  }, [onSuccess, onClose]);

  // Don't render if patient is still loading
  if (isLoadingPatient) {
    return null;
  }

  return (
    <>
      <FormSelectorSlidePane
        open={isFormSelectorOpen}
        onClose={() => {
          setIsFormSelectorOpen(false);
          setSelectedFormIds([]);
          onClose?.();
        }}
        patient={patient || undefined}
        subjectLanguage={subjectLanguage}
        subjectLanguageCap={subjectLanguageCap}
        selectedFormIds={selectedFormIds}
        onFormSelect={handleFormSelect}
        onCreateFormSession={handleCreateFormSession}
        onPreviewForm={handlePreviewForm}
      />

      <FormPreviewSlidePane
        open={isPreviewOpen}
        onClose={handleClosePreview}
        formId={previewFormId}
        showCustomizeLink={true}
        subjectLanguageCap={subjectLanguageCap}
        isCustomizing={isCloningForm}
        onCustomize={handleCustomizeForm}
      />

      <ReviewFormsSlidePane
        open={isReviewPaneOpen}
        onClose={() => {
          setIsReviewPaneOpen(false);
        }}
        patient={patient || undefined}
        patientId={patientId}
        selectedFormIds={selectedFormIds}
        forms={availableForms}
        onSuccess={handleSendSuccess}
        source={source}
      />
    </>
  );
});

SendFormsSlidePanels.displayName = 'SendFormsSlidePanels';

export default SendFormsSlidePanels;
